import { useEffect, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  FixButtonWrapper,
  FormWrapper,
} from '~/features/SimplifiedRegistration/styled'
import { infoSourceOptions } from '~/shared/config/constants'
import { phoneMaskOptions } from '~/shared/lib/maskito'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Button } from '~/shared/ui/Buttons'
import {
  FormProvider,
  MaskInput,
  SelectMultiple,
  TextInput,
} from '~/shared/ui/form'
import { FormValues } from './model'

const SMARTCAPTCHA_SCRIPT_ID = 'smartcaptcha-script'
const SMARTCAPTCHA_SCRIPT_SRC =
  'https://smartcaptcha.yandexcloud.net/captcha.js'

type FormProps = {
  onSuccess: (formValues: FormValues, captchaToken: string) => void
  form: UseFormReturn<FormValues>
  isSubmitting: boolean
}

export function Form({ onSuccess, form, isSubmitting }: FormProps) {
  const clientKey = import.meta.env.VITE_SMARTCAPTCHA_CLIENT_KEY

  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const pendingValuesRef = useRef<FormValues | null>(null)
  const onSuccessRef = useRef(onSuccess)

  useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  useEffect(() => {
    if (!clientKey) return

    const initWidget = () => {
      if (!containerRef.current || !window.smartCaptcha) return

      widgetIdRef.current = window.smartCaptcha.render(containerRef.current, {
        sitekey: clientKey,
        invisible: true,
        callback: (token: string) => {
          if (pendingValuesRef.current) {
            onSuccessRef.current(pendingValuesRef.current, token)
            pendingValuesRef.current = null
          }
          if (widgetIdRef.current !== null) {
            window.smartCaptcha?.reset(widgetIdRef.current)
          }
        },
        'error-callback': () => {
          pendingValuesRef.current = null
          snackbarEnqueued({
            message: 'Ошибка проверки CAPTCHA. Попробуйте ещё раз.',
            variant: 'danger',
          })
        },
      })
    }

    if (document.getElementById(SMARTCAPTCHA_SCRIPT_ID)) {
      initWidget()
      return
    }

    const script = document.createElement('script')
    script.id = SMARTCAPTCHA_SCRIPT_ID
    script.src = SMARTCAPTCHA_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = initWidget
    document.head.appendChild(script)

    return () => {
      if (widgetIdRef.current !== null) {
        window.smartCaptcha?.destroy(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [clientKey])

  const handleSubmit = (formValues: FormValues) => {
    if (!clientKey) {
      onSuccess(formValues, '')
      return
    }
    pendingValuesRef.current = formValues
    if (widgetIdRef.current !== null) {
      window.smartCaptcha?.execute(widgetIdRef.current)
    }
  }

  return (
    <FormProvider form={form} onSuccess={handleSubmit}>
      <FormWrapper>
        <TextInput name='lastName' label='Фамилия' capitalize />
        <TextInput name='firstName' label='Имя' capitalize />
        <MaskInput
          maskitoOptions={phoneMaskOptions}
          name='phone'
          label='Телефон'
        />
        <SelectMultiple
          field='infoSource'
          label='Откуда вы о нас узнали'
          modalTitle='Откуда вы о нас узнали'
          options={infoSourceOptions}
          name='infoSource'
        />
        <div ref={containerRef} />
        <FixButtonWrapper>
          <Button
            variant='limeFlooded'
            size='large'
            type='submit'
            fullWidth
            loading={isSubmitting}
          >
            Продолжить
          </Button>
        </FixButtonWrapper>
      </FormWrapper>
    </FormProvider>
  )
}
